import React, { useEffect, useMemo, useRef, useState } from 'react';
import { API_URL } from '../../../config/api';
import { uploadFilesWithProgress } from '../../../utils/uploadFilesWithProgress';
import PresignedImage from '../../../components/common/PresignedImage';
import {
  addEventExpense,
  deleteEventExpense,
  fetchEventExpenses,
  updateEventExpense,
} from '../services/eventService';
import {
  DEFAULT_EXPENSE_CATEGORIES,
  DEFAULT_EXPENSE_CONFIGURATION,
  expenseFieldEnabled,
  expenseFieldRequired,
  normalizeExpenseFields,
} from '../constants/expenseFields';

const inputClass = 'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500';
const labelClass = 'block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1';

const formatMoney = (amount, currency = 'INR') => {
  if (amount == null || amount === '') return '—';
  const value = Number(amount);
  if (!Number.isFinite(value)) return String(amount);
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 2 }).format(value);
  } catch {
    return `${currency} ${value.toFixed(2)}`;
  }
};

const ExpenseTrackerPanel = ({
  eventId,
  currentUserId,
  isCreator = false,
  configuration = {},
  onEntriesChanged,
  readOnly = false,
  mode = 'list',
  onAddEntry,
  onEditEntry,
  onSaved,
  initialEntry = null,
}) => {
  const fields = normalizeExpenseFields(configuration.fields || DEFAULT_EXPENSE_CONFIGURATION.fields);
  const currency = configuration.currency || 'INR';
  const categories = Array.isArray(configuration.categories) && configuration.categories.length
    ? configuration.categories
    : DEFAULT_EXPENSE_CATEGORIES;
  const fileRef = useRef(null);

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState({
    item: '',
    itemCategory: '',
    amount: '',
    boughtPlace: '',
    paymentStatus: 'PAID',
    imageUrl: '',
    imageFileName: '',
  });

  const loadEntries = async () => {
    if (!eventId) return;
    setLoading(true);
    try {
      const data = await fetchEventExpenses(eventId);
      setEntries(Array.isArray(data) ? data : []);
      onEntriesChanged?.(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not load expenses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (mode === 'form') return;
    loadEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, mode]);

  useEffect(() => {
    if (!initialEntry) return;
    setDraft({
      item: initialEntry.item || '',
      itemCategory: initialEntry.itemCategory || '',
      amount: initialEntry.amount != null ? String(initialEntry.amount) : '',
      boughtPlace: initialEntry.boughtPlace || '',
      paymentStatus: initialEntry.paymentStatus || 'PAID',
      imageUrl: initialEntry.imageUrl || '',
      imageFileName: initialEntry.imageFileName || '',
    });
  }, [initialEntry]);

  const totals = useMemo(() => {
    return entries.reduce((acc, row) => {
      const amount = Number(row.amount) || 0;
      acc.total += amount;
      if (String(row.paymentStatus || '').toUpperCase() === 'UDHAAR') acc.udhaar += amount;
      else acc.paid += amount;
      return acc;
    }, { total: 0, paid: 0, udhaar: 0 });
  }, [entries]);

  const show = (id) => expenseFieldEnabled(fields, id);
  const required = (id) => expenseFieldRequired(fields, id);
  const star = (id) => (required(id) ? ' *' : '');

  const handleImage = async (fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    setError('');
    try {
      const token = localStorage.getItem('token');
      const results = await uploadFilesWithProgress({
        apiUrl: API_URL,
        token,
        files: [file],
      });
      const data = results[0]?.data;
      if (!data?.fileUrl) throw new Error('Upload failed');
      setDraft((prev) => ({
        ...prev,
        imageUrl: data.fileUrl,
        imageFileName: data.fileName || file.name,
      }));
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Image upload failed');
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const missing = [];
    if (required('item') && !draft.item.trim()) missing.push('Item');
    if (required('itemCategory') && !draft.itemCategory.trim()) missing.push('Item category');
    if (required('amount') && draft.amount === '') missing.push('Amount');
    if (required('boughtPlace') && !draft.boughtPlace.trim()) missing.push('Bought place');
    if (required('paymentStatus') && !draft.paymentStatus) missing.push('Paid / Udhaar');
    if (required('image') && !draft.imageUrl) missing.push('Bill image');
    if (missing.length) {
      setError(`${missing.join(', ')} required`);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        item: draft.item.trim() || undefined,
        itemCategory: draft.itemCategory.trim() || undefined,
        amount: draft.amount === '' ? undefined : Number(draft.amount),
        boughtPlace: draft.boughtPlace.trim() || undefined,
        paymentStatus: draft.paymentStatus || undefined,
        imageUrl: draft.imageUrl || undefined,
        imageFileName: draft.imageFileName || undefined,
      };
      const saved = initialEntry?.id
        ? await updateEventExpense(eventId, initialEntry.id, payload)
        : await addEventExpense(eventId, payload);
      setEntries((prev) => {
        if (initialEntry?.id) {
          return prev.map((row) => (row.id === saved.id ? saved : row));
        }
        return [saved, ...prev];
      });
      onEntriesChanged?.(initialEntry?.id
        ? entries.map((row) => (row.id === saved.id ? saved : row))
        : [saved, ...entries]);
      setDraft({
        item: '',
        itemCategory: '',
        amount: '',
        boughtPlace: '',
        paymentStatus: 'PAID',
        imageUrl: '',
        imageFileName: '',
      });
      onSaved?.(saved);
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not save entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entry) => {
    if (!entry?.id) return;
    try {
      await deleteEventExpense(eventId, entry.id);
      setEntries((prev) => prev.filter((row) => row.id !== entry.id));
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not delete entry');
    }
  };

  const form = (
      <form onSubmit={handleSubmit} className={`space-y-3 ${mode === 'form' ? '' : 'rounded-lg bg-white/80 dark:bg-gray-900/40 p-3 border border-orange-100 dark:border-orange-900/40'}`}>
        {show('item') && (
          <label className={labelClass}>
            Item{star('item')}
            <input value={draft.item} onChange={(e) => setDraft((p) => ({ ...p, item: e.target.value }))} className={inputClass} placeholder="Tea, cab, printer ink…" />
          </label>
        )}
        {show('itemCategory') && (
          <label className={labelClass}>
            Item category{star('itemCategory')}
            <input
              list="expense-item-categories"
              value={draft.itemCategory}
              onChange={(e) => setDraft((p) => ({ ...p, itemCategory: e.target.value }))}
              className={inputClass}
              placeholder="Food, Travel…"
            />
            <datalist id="expense-item-categories">
              {categories.map((cat) => <option key={cat} value={cat} />)}
            </datalist>
          </label>
        )}
        {show('amount') && (
          <label className={labelClass}>
            Amount{star('amount')}
            <input type="number" min="0" step="0.01" value={draft.amount} onChange={(e) => setDraft((p) => ({ ...p, amount: e.target.value }))} className={inputClass} placeholder="0.00" />
          </label>
        )}
        {show('boughtPlace') && (
          <label className={labelClass}>
            Bought place{star('boughtPlace')}
            <input value={draft.boughtPlace} onChange={(e) => setDraft((p) => ({ ...p, boughtPlace: e.target.value }))} className={inputClass} placeholder="Shop / location" />
          </label>
        )}
        {show('paymentStatus') && (
          <div>
            <p className={labelClass}>Amount paid / Udhaar{star('paymentStatus')}</p>
            <div className="flex gap-2">
              {[
                { id: 'PAID', label: 'Paid' },
                { id: 'UDHAAR', label: 'Udhaar' },
              ].map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setDraft((p) => ({ ...p, paymentStatus: opt.id }))}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-semibold border ${
                    draft.paymentStatus === opt.id
                      ? opt.id === 'PAID'
                        ? 'bg-emerald-600 text-white border-emerald-600'
                        : 'bg-amber-500 text-white border-amber-500'
                      : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}
        {show('image') && (
          <div>
            <p className={labelClass}>Bill image{star('image')}</p>
            {draft.imageUrl ? (
              <div className="flex items-center gap-2">
                <PresignedImage fileKey={draft.imageUrl} alt="" className="w-14 h-14 rounded-lg object-cover" />
                <button type="button" className="text-xs text-rose-600 font-semibold" onClick={() => setDraft((p) => ({ ...p, imageUrl: '', imageFileName: '' }))}>
                  Remove
                </button>
              </div>
            ) : (
              <input ref={fileRef} type="file" accept="image/*" onChange={(e) => handleImage(e.target.files)} className="text-xs" />
            )}
          </div>
        )}
        {error && <p className="text-xs text-rose-600">{error}</p>}
        <button
          type="submit"
          disabled={saving}
          className="w-full rounded-lg bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold py-3 disabled:opacity-60"
        >
          {saving ? 'Saving…' : initialEntry?.id ? 'Save changes' : 'Save entry'}
        </button>
      </form>
  );

  if (mode === 'form') {
    return form;
  }

  return (
    <div className="rounded-xl border border-orange-200 dark:border-orange-900/50 bg-orange-50/40 dark:bg-orange-950/20 p-3 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-orange-900 dark:text-orange-100">Expense entries</p>
          <p className="text-[11px] font-semibold text-gray-700 dark:text-gray-200">
            Total {formatMoney(totals.total, currency)}
          </p>
        </div>
        {!readOnly && (
          <button
            type="button"
            onClick={onAddEntry}
            className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-orange-600 text-white hover:bg-orange-700"
          >
            Add entry
          </button>
        )}
      </div>

      {loading ? (
        <p className="text-xs text-gray-500 text-center py-3">Loading entries…</p>
      ) : entries.length === 0 ? (
        <p className="text-xs text-gray-500 text-center py-3">No expenses yet.</p>
      ) : (
        <ul className="space-y-2">
          {entries.map((row) => {
            const canManage = !readOnly && (String(row.userId) === String(currentUserId) || isCreator);
            const udhaar = String(row.paymentStatus || '').toUpperCase() === 'UDHAAR';
            return (
              <li key={row.id} className="rounded-lg bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-700 p-2.5">
                <div className="flex gap-2">
                  {row.imageUrl && (
                    <PresignedImage fileKey={row.imageUrl} alt="" className="w-12 h-12 rounded-md object-cover flex-shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{row.item || 'Expense'}</p>
                      {row.amount != null && (
                        <p className="text-sm font-bold tabular-nums text-gray-900 dark:text-white flex-shrink-0">
                          {formatMoney(row.amount, currency)}
                        </p>
                      )}
                    </div>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">
                      {[row.itemCategory, row.boughtPlace, row.userName].filter(Boolean).join(' · ')}
                    </p>
                    {row.paymentStatus && (
                      <span className={`inline-flex mt-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                        udhaar ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200' : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
                      }`}>
                        {udhaar ? 'Udhaar' : 'Paid'}
                      </span>
                    )}
                  </div>
                  {canManage && (
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <button type="button" onClick={() => onEditEntry?.(row)} className="text-[11px] text-orange-700 dark:text-orange-300 font-semibold">
                        Edit
                      </button>
                      <button type="button" onClick={() => handleDelete(row)} className="text-[11px] text-rose-600 font-semibold">
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default ExpenseTrackerPanel;
